import type { AdminMoneyHealthDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement, ReactNode } from 'react';

import { ClosingHoldsSection } from './ClosingHoldsSection';
import { EscalatedHoldsSection } from './EscalatedHoldsSection';
import { FailedPayoutsSection } from './FailedPayoutsSection';
import { StuckWebhooksSection } from './StuckWebhooksSection';

/**
 * The two-column body: holds on the left (they carry a capture story and need
 * the width), payouts and webhooks on the right.
 *
 * A section renders only when it has something in it. This is NOT the empty
 * state — when all four are zero the page renders the all-clear panel instead,
 * because four «Нет данных» cards read as a broken screen rather than a quiet
 * day. A partially quiet day just shows fewer cards.
 */
const Column = ({ basis, children }: { basis: string; children: ReactNode }): ReactElement => (
  <Box
    sx={{
      flex: `1 1 ${basis}`,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2.25,
    }}
  >
    {children}
  </Box>
);

export const MoneyHealthColumns = ({ data }: { data: AdminMoneyHealthDto }): ReactElement => (
  <Box sx={{ display: 'flex', gap: 2.25, alignItems: 'flex-start', flexWrap: 'wrap' }}>
    <Column basis="620px">
      {data.escalatedHolds.total > 0 ? <EscalatedHoldsSection list={data.escalatedHolds} /> : null}
      {data.closingHolds.total > 0 ? (
        <ClosingHoldsSection
          list={data.closingHolds}
          holdClosingLeadMs={data.thresholds.holdClosingLeadMs}
        />
      ) : null}
    </Column>
    <Column basis="380px">
      {data.failedPayouts.total > 0 ? <FailedPayoutsSection list={data.failedPayouts} /> : null}
      {data.stuckWebhooks.rowCount > 0 ? (
        <StuckWebhooksSection
          webhooks={data.stuckWebhooks}
          webhookStaleAfterMs={data.thresholds.webhookStaleAfterMs}
        />
      ) : null}
    </Column>
  </Box>
);
